import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  BarChart3,
  Settings,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  BookOpen,
  MessageSquare,
  ListChecks,
  PenLine,
  Star,
} from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";

/**
 * Page de documentation pour DooQuest (Formulaires)
 * Style moderne inspiré de la documentation Quiz
 */
export default function FormPollsDocumentation() {
  const { t } = useTranslation("forms");
  return (
    <div className="flex flex-col min-h-screen bg-[#030712] text-white">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="form" />
        <div className="flex-1">
          <div className="max-w-4xl mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
                <BookOpen className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-violet-300">
                  {t("guide.doc.badge")}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-4">
                {t("guide.doc.title")}
              </h1>
              <p className="text-lg text-gray-400">{t("guide.doc.subtitle")}</p>
            </div>

            {/* Getting Started */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-violet-400" />
                {t("guide.doc.quickstart_title")}
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl hover:border-violet-500/30 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold">
                      1
                    </div>
                    <h3 className="font-semibold">
                      {t("guide.doc.quickstart_1_title")}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.quickstart_1_desc")}
                  </p>
                </div>

                <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl hover:border-violet-500/30 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold">
                      2
                    </div>
                    <h3 className="font-semibold">
                      {t("guide.doc.quickstart_2_title")}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.quickstart_2_desc")}
                  </p>
                </div>

                <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl hover:border-violet-500/30 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold">
                      3
                    </div>
                    <h3 className="font-semibold">
                      {t("guide.doc.quickstart_3_title")}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.quickstart_3_desc")}
                  </p>
                </div>

                <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl hover:border-violet-500/30 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold">
                      4
                    </div>
                    <h3 className="font-semibold">
                      {t("guide.doc.quickstart_4_title")}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.quickstart_4_desc")}
                  </p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6">
                {t("guide.doc.features_title")}
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {t("guide.doc.feature_ai_title")}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {t("guide.doc.feature_ai_desc")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                    <ListChecks className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {t("guide.doc.feature_types_title")}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {t("guide.doc.feature_types_desc")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {t("guide.doc.feature_logic_title")}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {t("guide.doc.feature_logic_desc")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {t("guide.doc.feature_analytics_title")}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {t("guide.doc.feature_analytics_desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types de questions */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6">
                {t("guide.doc.qtypes_title")}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_single_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_single_desc")}
                  </p>
                </div>
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_multi_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_multi_desc")}
                  </p>
                </div>
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_text_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_text_desc")}
                  </p>
                </div>
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_rating_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_rating_desc")}
                  </p>
                </div>
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_nps_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_nps_desc")}
                  </p>
                </div>
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                  <h4 className="font-medium text-violet-300 mb-2">
                    {t("guide.doc.qtype_likert_title")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.qtype_likert_desc")}
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-violet-400" />
                {t("guide.doc.faq_title")}
              </h2>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">
                    {t("guide.doc.faq_logic_q")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.faq_logic_a")}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">
                    {t("guide.doc.faq_multistep_q")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.faq_multistep_a")}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">
                    {t("guide.doc.faq_export_q")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.faq_export_a")}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">
                    {t("guide.doc.faq_insights_q")}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {t("guide.doc.faq_insights_a")}
                  </p>
                </div>
              </div>
            </div>

            {/* Documentation avancée */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {t("guide.doc.advanced_title")}
              </h2>
              <p className="text-gray-400 mb-4">
                {t("guide.doc.advanced_desc")}
              </p>
              <Link
                to="/form/documentation/advanced"
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-400 hover:bg-violet-500/30 transition-colors"
              >
                {t("guide.doc.advanced_link")}
              </Link>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-2xl p-8">
              <h2 className="text-xl font-semibold mb-4">
                {t("guide.doc.quicklinks_title")}
              </h2>
              <div className="grid gap-3 md:grid-cols-3">
                <Link
                  to="/form/dashboard"
                  className="block text-violet-400 hover:text-violet-300 transition-colors"
                >
                  {t("guide.doc.quicklink_dashboard")}
                </Link>
                <Link
                  to="/form/create"
                  className="block text-violet-400 hover:text-violet-300 transition-colors"
                >
                  {t("guide.doc.quicklink_create")}
                </Link>
                <Link
                  to="/pricing"
                  className="block text-violet-400 hover:text-violet-300 transition-colors"
                >
                  {t("guide.doc.quicklink_pricing")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
